import { useState, useMemo } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import AppError from 'exceptions/AppError';
import { ptpCatchError } from 'services/sentry.services';
import { CategoryState } from './RatingForm.interface';
import { updateRate } from '../../../../../services/rating.services';
import { Rate } from '../../../../../interfaces/Rate.interface';
import { isApiError } from '../../../../../utils/fetcher.utils';
import { Configs } from '../../../../../interfaces/WidgetConfigs.interface';
import { getRatingCategoryConfig } from '../../../../../utils/shipmentRating';

interface ratingCallbacks {
  onRateSuccess?: (rate: Rate) => void;
}

const useInitCategories = (
  widgetConfig: Configs,
  lastRating: Rate,
): CategoryState[] => {
  const { t } = useTranslation();

  const initialRatingCategories = useMemo<CategoryState[]>(() => {
    const ratingCategoryConfig = getRatingCategoryConfig(
      lastRating.value,
      widgetConfig,
    );

    return [
      ...ratingCategoryConfig.standard.map((category) => {
        const { key } = category;

        return {
          label: t(key),
          value: key,
          selected: lastRating.categories?.includes(key),
        };
      }),
      ...ratingCategoryConfig.custom
        .filter((category) => category.isEnabled)
        .map((category) => {
          const { value } = category;

          return {
            label: value,
            value,
            selected: lastRating.categories?.includes(category.value),
          };
        }),
    ];
  }, []);

  return initialRatingCategories;
};

const useRatingCategories = (widgetConfig: Configs, lastRating: Rate) => {
  const initialUiCategories = useInitCategories(widgetConfig, lastRating);

  const [categories, setCategories] =
    useState<CategoryState[]>(initialUiCategories);

  const updateCategory = (category, newValue) => {
    const categoryIndex = categories.indexOf(category);
    const newCategories = [...categories];
    newCategories[categoryIndex] = { ...category, ...newValue };
    setCategories(newCategories);
  };

  const onClick = (category: CategoryState): void => {
    updateCategory(category, {
      selected: !category.selected,
    });
  };

  return {
    categories,
    onClick,
  };
};

export const useRatingForm = (
  widgetConfigs,
  lastRating,
  callbacks: ratingCallbacks = {},
) => {
  const serializeCategories = (categories: CategoryState[]): string[] =>
    categories.filter((cat) => cat.selected).map((cat) => cat.value);
  const { categories, onClick: onClickCategory } = useRatingCategories(
    widgetConfigs,
    lastRating,
  );
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const onSubmit = async () => {
    try {
      const newRate = await updateRate({
        ...lastRating,
        categories: serializeCategories(categories),
        comment,
      });

      callbacks.onRateSuccess?.(newRate);
    } catch (e) {
      if (!(e instanceof AppError)) {
        ptpCatchError(e);
      }
      setError(e.message);
    }
  };

  return {
    comment,
    onChangeComment: setComment,
    categories,
    onClickCategory,
    onSubmit,
    error,
  };
};
